import { createContext, useContext } from 'react';

// Approval and Connect cards report their decision here. The chat uses it to pick
// the turn back up when the decision comes after Atlas stopped waiting on the card
// (see onCardDecided in AtlasChat). Cards outside a chat get null and skip it.
export const CardDecisionContext = createContext(null);

export const useCardDecided = () => useContext(CardDecisionContext);

const CARD_LISTS = ['emailApprovals', 'appApprovals', 'appConnects'];
const WENT_AHEAD = ['done', 'sent', 'scheduled', 'failed'];

export const messageHoldsCard = (message, actionId) => CARD_LISTS.some(
  (k) => Array.isArray(message?.[k]) && message[k].some((a) => a.actionId === actionId),
);

/** The message with this card's new status. */
export const withCardStatus = (message, actionId, status) => {
  if (!messageHoldsCard(message, actionId)) return message;
  const next = { ...message };
  CARD_LISTS.forEach((k) => {
    if (Array.isArray(message[k])) next[k] = message[k].map((a) => (a.actionId === actionId ? { ...a, status } : a));
  });
  return next;
};

/**
 * What to send to pick the turn back up after a card was decided, or null.
 * Only when Atlas isn't running, only for a card on the latest reply, only once
 * none of that reply's cards is still open (a batch continues once, after the last),
 * and only if something went ahead (all declined -> nothing to carry on with).
 */
export function continuationAfterCard(messages, decision, { streaming = false, alreadyContinued = () => false } = {}) {
  const { actionId, kind, status, app } = decision || {};
  if (streaming || !actionId || !status || status === 'pending') return null;
  let latest = null;
  for (let i = (messages || []).length - 1; i >= 0; i -= 1) {
    if (messages[i]?.role === 'assistant') {
      latest = messages[i];
      break;
    }
  }
  if (!latest || !messageHoldsCard(latest, actionId) || alreadyContinued(latest.id)) return null;
  const cards = CARD_LISTS.flatMap((k) => (Array.isArray(latest[k]) ? latest[k] : []))
    .map((a) => (a.actionId === actionId ? { ...a, status } : a));
  if (cards.some((a) => (a.status || 'pending') === 'pending')) return null;
  if (!cards.some((a) => WENT_AHEAD.includes(a.status))) return null;
  return {
    messageId: latest.id,
    text: kind === 'app_connect' && status === 'done' ? `Connected ${app || 'the app'}. Carry on.` : 'Approved. Carry on.',
  };
}
